import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
  type Timestamp,
} from 'firebase/firestore';
import { appConfig } from '@/config/app';
import { getFirestoreDb } from '@/services/firebase';
import { ITEMS_COLLECTION } from '@/modules/inventory/services/constants';
import {
  buildTimestampPayload,
  timestampToDate,
} from '@/modules/inventory/services/firestoreMapper';
import type { InventoryItem, InventoryItemInput } from '@/modules/inventory/types';
import { recordEntityWrite, toAuditSnapshot } from '@/services/audit';
import { withFirestoreError } from '@/shared/utils/firestoreError';
import {
  normalizeInventoryMode,
  normalizeInventoryStatus,
  type LegacyItemFields,
} from '@/modules/inventory/types/item';

type ItemFirestoreDoc = LegacyItemFields & {
  companyId: string;
  sku: string;
  name: string;
  description: string;
  categoryId: string;
  subcategoryId: string | null;
  customFieldValues: Record<string, unknown>;
  trackStock: boolean;
  stockQuantity: number;
  stockUnit: string;
  currentLocationId?: string | null;
  lastMovementAt?: string | null;
  lastInventoryCheckAt?: string | null;
  createdAt: Timestamp;
  updatedAt: Timestamp;
};

function mapItem(id: string, data: ItemFirestoreDoc): InventoryItem {
  return {
    id,
    companyId: data.companyId,
    sku: data.sku,
    name: data.name,
    description: data.description ?? '',
    categoryId: data.categoryId,
    subcategoryId: data.subcategoryId,
    mode: normalizeInventoryMode(data),
    status: normalizeInventoryStatus(data),
    customFieldValues: data.customFieldValues ?? {},
    trackStock: data.trackStock ?? false,
    stockQuantity: data.stockQuantity ?? 0,
    stockUnit: data.stockUnit ?? 'un',
    currentLocationId: data.currentLocationId ?? null,
    lastMovementAt: data.lastMovementAt ?? null,
    lastInventoryCheckAt: data.lastInventoryCheckAt ?? null,
    createdAt: timestampToDate(data.createdAt),
    updatedAt: timestampToDate(data.updatedAt),
  };
}

export async function listItems(
  companyId: string = appConfig.companyId,
): Promise<InventoryItem[]> {
  const q = query(
    collection(getFirestoreDb(), ITEMS_COLLECTION),
    where('companyId', '==', companyId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => mapItem(d.id, d.data() as ItemFirestoreDoc))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export async function getItem(id: string): Promise<InventoryItem | null> {
  const snap = await getDoc(doc(getFirestoreDb(), ITEMS_COLLECTION, id));
  if (!snap.exists()) return null;
  return mapItem(snap.id, snap.data() as ItemFirestoreDoc);
}

export async function saveItem(
  input: InventoryItemInput,
  id?: string,
): Promise<InventoryItem> {
  return withFirestoreError(async () => {
  const ref = id
    ? doc(getFirestoreDb(), ITEMS_COLLECTION, id)
    : doc(collection(getFirestoreDb(), ITEMS_COLLECTION));
  const existing = await getDoc(ref);
  const before = existing.exists()
    ? toAuditSnapshot(mapItem(existing.id, existing.data() as ItemFirestoreDoc))
    : undefined;
  const payload = {
    companyId: appConfig.companyId,
    sku: input.sku.trim(),
    name: input.name.trim(),
    description: input.description.trim(),
    categoryId: input.categoryId,
    subcategoryId: input.subcategoryId,
    mode: input.mode,
    status: input.status,
    customFieldValues: input.customFieldValues,
    trackStock: input.trackStock,
    stockQuantity: input.trackStock ? input.stockQuantity : 0,
    stockUnit: input.stockUnit.trim(),
    ...(existing.exists()
      ? {}
      : {
          currentLocationId: null,
          lastMovementAt: null,
          lastInventoryCheckAt: null,
        }),
    ...buildTimestampPayload(!existing.exists()),
  };
  await setDoc(ref, payload, { merge: true });
  const saved = await getDoc(ref);
  if (!saved.exists()) throw new Error('No se pudo guardar el artículo.');
  const result = mapItem(saved.id, saved.data() as ItemFirestoreDoc);
  recordEntityWrite({
    entityType: 'inventoryItem',
    entityId: result.id,
    isCreate: !existing.exists(),
    ...(before !== undefined ? { before } : {}),
    after: toAuditSnapshot(result, ['customFieldValues']),
    ...(Object.keys(input.customFieldValues).length > 0
      ? { metadata: { hasCustomFields: true } }
      : {}),
  });
  return result;
  }, 'item');
}
